import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AuxInstallerPassModel,
  InstallerPassModel,
  InstallerPassTypeEnum
} from '@bo-client/shared/models/installerPassDto';
import { DocumentDto, DocumentDtoModel, AnswerModel } from '@bo-client/shared/models/documentDto';
import { AreaTypesModel } from '@bo-client/shared/models/areaTypes';
import {
  AuxTmInstallerPassForCustomerCare,
  CreateOrUpdateTmUserDto
} from '@bo-client/shared/models/createOrUpdateTmUserDto';
import { ContactData } from '@bo-client/shared/models/contactData';
import { TmUserFormValidationService } from '@bo-client/technician-management/tm-user/tm-user-form-validation.service';
import { QuestionModel } from '@bo-client/technician-management/config/tm-document/tm-config-document.model';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { RegexToCheckEMail, RegexToCheckPassword } from '@bo-client/shared/forms/forms.validators';

@Injectable({
  providedIn: 'root'
})
export class TmUserService {
  public form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private tmUserFormValidationService: TmUserFormValidationService,
    private validatorService: ValidatorsService
  ) {}

  createFormTmRegistration(data: CreateOrUpdateTmUserDto) {
    return this.fb.group({
      // companyLocations: ['', Validators.required],
      companyContactData: this.companyContactData(data.contactData),
      personContactData: this.personContactData(data.contactData),
      userAccess: this.userAccess(data),
      conditions: this.conditionsData()
    });
  }

  createFormTmCompanyLocation() {
    return this.fb.group({
      companyLocations: [null as number, Validators.required],
      areaType: [null as number],
      passType: [null as InstallerPassTypeEnum]
    });
  }

  editFormTmNewuser(data: CreateOrUpdateTmUserDto) {
    return this.fb.group({
      companyLocations: ['', Validators.required],
      areaType: [''],
      userAccess: this.userAccess(data),
      personContactData: this.personContactData(data.contactData),
      conditions: this.conditionsData()
    });
  }

  createFormTmNewuser(data: CreateOrUpdateTmUserDto) {
    return this.fb.group({
      // companyLocations: ['', Validators.required],
      // areaType: [''],
      userAccess: this.userAccess(data),
      personContactData: this.personContactData(data.contactData),
      conditions: this.conditionsData()
    });
  }

  companyContactData(data: ContactData) {
    return this.fb.group({
      company: [data.company, Validators.required],
      companyAddition: [data.companyAddition],
      street: [data.street, Validators.required],
      streetNo: [data.streetNo, Validators.required],
      zipCode: [data.zipCode, Validators.required],
      city: [data.city, Validators.required]
    });
  }

  personContactData(data: ContactData) {
    return this.fb.group({
      firstName: [data.firstName, Validators.required],
      lastName: [data.lastName, Validators.required],
      phone: [data.phone, Validators.required],
      mobile: [data.mobile, []]
    });
  }

  userAccess(data: CreateOrUpdateTmUserDto) {
    return this.fb.group(
      {
        mail: [data.mail, [Validators.required, Validators.pattern(RegexToCheckEMail)]],
        confirmMail: [data.mail, [Validators.required]],
        password: [data.password, [Validators.required, Validators.pattern(RegexToCheckPassword)]]
      },
      {
        validator: this.validatorService.mustMatch('mail', 'confirmMail')
      }
    );
  }

  conditionsData() {
    return this.fb.group(
      {
        acceptConditions: [false, [Validators.required, this.tmUserFormValidationService.mustTrue]]
        // acceptOtherConditions: [false, [Validators.required, this.tmUserFormValidationService.mustTrue]]
      },
      {}
    );
  }

  createPassDataForm(data: InstallerPassModel, isAdminUser: boolean) {
    return this.fb.group(
      {
        passNummer: [data.passNummer, isAdminUser ? Validators.required : []],
        networkOperator: [data.networkOperator],
        idarea: [data.idarea],
        status: [data.status],
        approvedByInstallerCommittee: [data.approvedByInstallerCommittee],
        type: [data.type],
        applicationdate: [data.applicationdate, isAdminUser ? Validators.required : []],
        expirationdate: [data.expirationdate, isAdminUser ? Validators.required : []],
        shortDescription: [data.shortDescription]
      },
      {}
    );
  }

  get isValid(): boolean {
    if (!this.form.valid) {
      this.tmUserFormValidationService.validateAllFormFields(this.form);
      return false;
    }
    return true;
  }

  createSubFormDocuments(
    listDocTMInstallateur: QuestionModel[],
    currentPass: InstallerPassModel,
    isAdminUser: boolean,
    isRenewedDocument: boolean,
    isImportedUser?: boolean
  ) {
    let docsForCurrentPass: AnswerModel[] = new Array<AnswerModel>();
    docsForCurrentPass = currentPass.answers.filter(
      (ele) =>
        ele.docType.toString().toLowerCase().indexOf('renew') == -1 && ele.docRenewed == isRenewedDocument
    );
    // Renewed Answer have the same docType (doc, question, license) and the isRenewed is true
    if (docsForCurrentPass.length == 0 && !isRenewedDocument && !isImportedUser) {
      docsForCurrentPass = this.loadNewAnswersFromQuestions(
        docsForCurrentPass,
        listDocTMInstallateur,
        currentPass,
        'renew',
        false
      );
    }
    return this.fb.group({
      answers: this.fb.array(docsForCurrentPass.map((data) => this.answersSubForm(data, isAdminUser)))
    });
  }

  createSubFormDocumentsToRenew(
    listDocTMInstallateur: QuestionModel[],
    currentPass: InstallerPassModel,
    isAdminUser: boolean
  ) {
    let renewDocsForCurrentPass: AnswerModel[] = new Array<AnswerModel>();
    renewDocsForCurrentPass = currentPass.answers.filter(
      (ele) => ele.docType.toString().toLowerCase().indexOf('renew') != -1
    );
    if (renewDocsForCurrentPass.length == 0) {
      renewDocsForCurrentPass = this.loadNewAnswersFromQuestions(
        renewDocsForCurrentPass,
        listDocTMInstallateur,
        currentPass,
        'renew',
        true
      );
    }
    return this.fb.group({
      answersExpired: this.fb.array(
        renewDocsForCurrentPass.map((data) => this.answersSubForm(data, isAdminUser))
      )
    });
  }

  loadNewAnswersFromQuestions(
    listAnswer: AnswerModel[],
    listDocTMInstallateur: QuestionModel[],
    currentPass: InstallerPassModel,
    textToSearchInDocType: string,
    isEqual: boolean
  ): AnswerModel[] {
    const now = new Date();
    let docsConfigForCurrentArea: QuestionModel[] = new Array<QuestionModel>();
    docsConfigForCurrentArea = listDocTMInstallateur
      .sort((a, b) => (a.sortPosition < b.sortPosition ? -1 : a.sortPosition > b.sortPosition ? 1 : 0))
      .filter(
        (ele) =>
          (ele.common || ele.idAreaType == currentPass.idarea) &&
          ele.installerType == InstallerPassTypeEnum[currentPass.type] &&
          (!ele.validFrom || new Date(ele.validFrom) < now) &&
          (!ele.validTo || new Date(ele.validTo) > now) &&
          (isEqual
            ? ele.docType.toString().toLowerCase().indexOf(textToSearchInDocType) != -1
            : ele.docType.toString().toLowerCase().indexOf(textToSearchInDocType) == -1)
      );
    docsConfigForCurrentArea.forEach((ele) => {
      listAnswer.push(this.newDocAnswer(ele));
    });
    return listAnswer;
  }

  newDocAnswer(ele: QuestionModel): AnswerModel {
    const newDoc: AnswerModel = new AnswerModel();
    newDoc.name = ele.name;
    newDoc.description = ele.description;
    newDoc.docType = ele.docType;
    newDoc.docReference = ele.id;
    newDoc.required = ele.required;
    newDoc.attachments = new Array<DocumentDto>();
    const newHapDoc: DocumentDtoModel = new DocumentDtoModel();
    newHapDoc.displayName = 'fileName';
    newDoc.attachments.push(newHapDoc);
    return newDoc;
  }

  areaTypes(data: AreaTypesModel) {
    return this.fb.group({
      id: [data.id, Validators.required],
      name: [data.name],
      description: [data.description]
    });
  }

  answersSubForm(tmDoc: AnswerModel, isAdminUser: boolean) {
    return this.fb.group({
      id: [tmDoc.id],
      sortPosition: [tmDoc.sortPosition],
      required: [tmDoc.required],
      common: [tmDoc.common],
      networkOperator: [tmDoc.networkOperator],
      validated: [
        tmDoc.validated,
        isAdminUser && tmDoc.docType.toString().toLowerCase().indexOf('doc') != -1
          ? [Validators.required, this.mustTrue]
          : []
      ],
      docReference: [tmDoc.docReference],
      docType: [tmDoc.docType],
      isAnswered: [
        tmDoc.isAnswered,
        !isAdminUser && tmDoc.docType.toString().toLowerCase().indexOf('question') != -1
          ? [Validators.required, this.mustTrue]
          : []
      ],
      name: [tmDoc.name],
      docRenewed: [tmDoc.docRenewed],
      description: [tmDoc.description],
      comment: [tmDoc.comment],
      validFrom: [tmDoc.validFrom],
      validTo: [tmDoc.validTo],
      attachments: this.fb.array(
        tmDoc.attachments.map((data) =>
          this.documentsSubForm(data, isAdminUser, tmDoc.docType, tmDoc.required)
        )
      )
    });
  }

  documentsSubForm(doc: DocumentDto, isAdminUser: boolean, docType: string, required = false) {
    return this.fb.group({
      id: [doc.id],
      cokDocId: [doc.cokDocId],
      displayName: [doc.displayName],
      fileName: [doc.fileName],
      url: [doc.url, !isAdminUser && docType === 'doc' && required ? Validators.required : []],
      tag: [doc.tag]
    });
  }

  createFormDocuments(data: QuestionModel) {
    return this.fb.group({
      id: [data.id],
      sortPosition: [data.sortPosition],
      required: [data.required],
      common: [data.common],
      header: [data.header],
      idCompanyLocation: [data.idCompanyLocation],
      idAreaType: [data.idAreaType],
      installerType: [data.installerType],
      docType: [data.docType],
      name: [data.name],
      description: [data.description],
      headerText: [data.headerText],
      validFrom: [data.validFrom],
      validTo: [data.validTo]
    });
  }

  showAllPasses(userTMList: CreateOrUpdateTmUserDto[]): AuxTmInstallerPassForCustomerCare[] {
    const auxInstallerPasses: AuxTmInstallerPassForCustomerCare[] =
      new Array<AuxTmInstallerPassForCustomerCare>();
    userTMList.forEach((xUser) => {
      xUser.passes.forEach((xPass) => {
        const newAuxPass: AuxTmInstallerPassForCustomerCare = new AuxTmInstallerPassForCustomerCare();
        newAuxPass.tmUser = xUser;
        const newAuxInsPass: AuxInstallerPassModel = new AuxInstallerPassModel();
        newAuxInsPass.pass = xPass;
        newAuxInsPass.auxDocTotal = xPass.answers.length;
        newAuxInsPass.auxDocLoaded = xPass.answers.filter((xDoc) => xDoc.attachments[0].url != null).length;
        newAuxInsPass.auxPorcLoaded = Math.round(
          (newAuxInsPass.auxDocLoaded / newAuxInsPass.auxDocTotal) * 100
        );
        newAuxPass.auxPass = newAuxInsPass;
        auxInstallerPasses.push(newAuxPass);
      });
    });
    return auxInstallerPasses;
  }

  mustTrue(control) {
    const enteredValue = control.value;
    return !enteredValue ? { mustTrue: true } : null;
  }

  calcDaysToRenew(expirationdate: Date): number {
    return Math.trunc((new Date(expirationdate).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
  }
}
